import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ShieldCheck, Mail } from "lucide-react";
import { HansaLogo, PoweredByBadge } from "../components/HansaLogo";

// Privacy policy page — public URL https://thecropdoctor.com/privacy
// Required by Google Play Store, Apple App Store, and most jurisdictions.
// Keep the content honest and specific to what Hansa actually does.
export default function PrivacyPolicyPage() {
  const lastUpdated = "May 11, 2026";
  const contactEmail = "support@thecropdoctor.com";

  return (
    <div className="min-h-screen bg-[#FDFBF7] text-[#1C1C1C]">
      <header className="max-w-3xl mx-auto px-5 sm:px-8 py-5 flex items-center justify-between">
        <Link to="/" data-testid="privacy-back" className="flex items-center gap-1 text-[#1B4332] font-semibold hover:underline">
          <ArrowLeft className="w-4 h-4" /> Home
        </Link>
        <HansaLogo size={40} />
      </header>

      <main className="max-w-3xl mx-auto px-5 sm:px-8 pb-16 prose-sm">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-12 rounded-2xl bg-[#1B4332] flex items-center justify-center">
            <ShieldCheck className="w-6 h-6 text-[#D4A373]" />
          </div>
          <div>
            <h1 className="cd-heading text-3xl sm:text-4xl font-extrabold leading-tight">Privacy Policy</h1>
            <div className="text-xs text-[#52796F] mt-1">Last updated: {lastUpdated}</div>
          </div>
        </div>

        <Section title="Who we are">
          <p>
            Hansa (“we”, “us”, “our”) is a crop-diagnosis and farm-advisory product operated
            by <b>Ramkishan Agri Innovate</b> in India. We serve farmers via the web app at
            thecropdoctor.com and via the Hansa Android app on Google Play. This Privacy
            Policy explains what we collect, why, and the choices you have.
          </p>
        </Section>

        <Section title="What we collect">
          <ul>
            <li><b>Account:</b> mobile number (for OTP login) or email + password, name, and the location you optionally type when signing up.</li>
            <li><b>Photos you upload</b> of crops, chemical labels and similar farm imagery, plus any voice/text crop hint you provide.</li>
            <li><b>Field data</b> you choose to save: field name, crop, optional location.</li>
            <li><b>Diagnosis results, chemical-check reports, soil reports, weather queries and chats</b> you generate inside the app — so you can review them later.</li>
            <li><b>Device & technical info:</b> approximate GPS (only when you tap “use my location”), browser/app version, and basic error logs to keep the app reliable.</li>
            <li><b>We do NOT collect</b> contacts, SMS history, calendar, microphone (other than the on-device voice-to-text for crop hint), or any data we don’t need.</li>
          </ul>
        </Section>

        <Section title="Why we use it">
          <ul>
            <li>Run AI diagnosis on your photos to give you treatment, medicine and soil advice.</li>
            <li>Show weather + soil + satellite data centred on your field.</li>
            <li>Keep a per-user history of scans, chemical checks and soil reports so you can refer back.</li>
            <li>Send the one-time SMS code you request when logging in.</li>
            <li>Operate, secure and improve Hansa, including anonymised usage analytics for the admin dashboard.</li>
          </ul>
        </Section>

        <Section title="Who we share it with (third-party processors)">
          <p>We share the minimum necessary data with these processors. We do <b>not</b> sell your data or share it for advertising.</p>
          <ul>
            <li><b>Twilio</b> — to deliver SMS OTP. They receive your mobile number and the OTP message body only.</li>
            <li><b>Google (Gemini) / Anthropic (Claude) / OpenAI</b> — to run the AI vision and text models. They receive the photo and prompt for diagnosis only; we do not send your name, mobile number or email to them.</li>
            <li><b>ISRIC SoilGrids</b> and <b>Open-Meteo</b> — to fetch soil and weather data for your chosen GPS coordinates. They receive only lat/lon, not your identity.</li>
            <li><b>Esri (ArcGIS World Imagery)</b> — to render satellite map tiles in the soil report.</li>
            <li><b>MongoDB Atlas</b> — encrypted data storage (Mumbai region preferred).</li>
            <li><b>Hosting / CDN</b> — Emergent and Cloudflare for serving the app.</li>
          </ul>
        </Section>

        <Section title="Where data is stored & how long">
          <p>
            Data is stored in encrypted MongoDB databases. Photos and diagnosis results are
            kept while your account is active so your history is useful. You can delete any
            scan from inside the app at any time. If you want your entire account and all
            associated data deleted, email us at <a className="text-[#1B4332] underline" href={`mailto:${contactEmail}`}>{contactEmail}</a> and we will action it within 30 days.
          </p>
        </Section>

        <Section title="Your choices">
          <ul>
            <li><b>Location:</b> optional. The browser/Android prompts you each time. You can deny it; the soil report will fall back to a regional estimate based on the location you typed when signing up (if any).</li>
            <li><b>Camera & photos:</b> only used when you actively tap upload/scan.</li>
            <li><b>Account deletion:</b> email <a className="text-[#1B4332] underline" href={`mailto:${contactEmail}`}>{contactEmail}</a> from your registered email or mobile.</li>
            <li><b>Marketing:</b> we do not send marketing SMS or email. Transactional OTP / order messages only.</li>
          </ul>
        </Section>

        <Section title="Children">
          <p>
            Hansa is intended for use by adult farmers. We do not knowingly collect data from
            anyone under 13. If you believe a child has provided us data, please email us and
            we will delete it.
          </p>
        </Section>

        <Section title="Security">
          <p>
            We use HTTPS for all transport, bcrypt for password hashing, and short-lived JWTs
            for authentication. While no system is 100% secure, we follow industry-standard
            practices to keep your data safe.
          </p>
        </Section>

        <Section title="Changes to this policy">
          <p>
            If we materially change this policy we will update the “Last updated” date above
            and, for material changes, show a banner in the app for at least 14 days before
            the change takes effect.
          </p>
        </Section>

        <Section title="Contact">
          <div className="bg-white border border-[#E5E0D8] rounded-2xl p-5 flex items-center gap-3 not-prose">
            <Mail className="w-5 h-5 text-[#1B4332]" />
            <div className="text-sm">
              <div className="font-bold">Ramkishan Agri Innovate</div>
              <a className="text-[#1B4332] underline" href={`mailto:${contactEmail}`}>{contactEmail}</a>
            </div>
          </div>
        </Section>

        <footer className="mt-10 pt-6 border-t border-[#E5E0D8] flex flex-col items-center gap-1.5 text-sm text-[#52796F]">
          <PoweredByBadge />
          <div className="text-xs">© {new Date().getFullYear()} Hansa · Ramkishan Agri Innovate</div>
        </footer>
      </main>
    </div>
  );
}

const Section = ({ title, children }) => (
  <section className="mt-6">
    <h2 className="cd-heading font-bold text-xl text-[#1B4332]">{title}</h2>
    <div className="mt-2 text-[#2D3A35] text-[15px] leading-relaxed space-y-2 [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:space-y-1.5 [&_ul]:mt-1.5">{children}</div>
  </section>
);
